import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { DollarSign, FileText, TrendingUp, Percent, BarChart3, Clock } from 'lucide-react';
import { formatCurrency, formatPercentage, formatNumber } from '@/lib/utils/format';
import { montoCorto } from '@/features/inicio/lib/resumen-de-inicio';
import type { AnalyticsKPIs } from '../types/analytics.types';

interface KpiCardsProps {
  data: AnalyticsKPIs | null;
  isLoading: boolean;
}

/*
  Los montos van cortos («USD 228,5 k») con el completo en el `title`: en seis
  columnas, «USD 228.512,40» no entraba y se leía «USD 22…».
*/
const KPI_CONFIG = [
  {
    key: 'totalRevenue' as const,
    label: 'Ingresos',
    icon: DollarSign,
    color: 'text-ok',
    bg: 'bg-ok-suave',
    format: (v: number) => montoCorto(v),
    completo: (v: number) => formatCurrency(v),
  },
  {
    key: 'totalQuotes' as const,
    label: 'Cotizaciones',
    icon: FileText,
    color: 'text-info',
    bg: 'bg-info-suave',
    format: (v: number) => formatNumber(v, 0),
  },
  {
    key: 'winRate' as const,
    label: 'Tasa de cierre',
    icon: TrendingUp,
    color: 'text-ok',
    bg: 'bg-ok-suave',
    format: (v: number) => formatPercentage(v, 1),
  },
  {
    key: 'avgMargin' as const,
    label: 'Margen promedio',
    icon: Percent,
    color: 'text-ia',
    bg: 'bg-ia-suave',
    format: (v: number) => formatPercentage(v, 1),
  },
  {
    key: 'avgDealSize' as const,
    label: 'Ticket promedio',
    icon: BarChart3,
    color: 'text-info',
    bg: 'bg-info-suave',
    format: (v: number) => montoCorto(v),
    completo: (v: number) => formatCurrency(v),
  },
  {
    key: 'avgCycleTimeDays' as const,
    label: 'Ciclo promedio',
    icon: Clock,
    color: 'text-alerta',
    bg: 'bg-alerta-suave',
    format: (v: number) => `${formatNumber(v, 0)} ${Math.round(v) === 1 ? 'día' : 'días'}`,
  },
];

export function KpiCards({ data, isLoading }: KpiCardsProps) {
  return (
    <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-6">
      {KPI_CONFIG.map((kpi) => {
        const Icon = kpi.icon;
        const valor = data ? data[kpi.key] : null;
        return (
          <Card key={kpi.key} className="p-4">
            <CardContent className="p-0">
              <div className="flex items-start justify-between gap-2">
                <p className="text-sm text-muted-foreground">{kpi.label}</p>
                <div
                  className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${kpi.bg}`}
                >
                  <Icon className={`h-4 w-4 ${kpi.color}`} aria-hidden="true" />
                </div>
              </div>
              {isLoading ? (
                <Skeleton className="mt-1 h-7 w-20" />
              ) : (
                <p
                  className="mt-1 whitespace-nowrap font-titulo text-2xl font-semibold tabular-nums"
                  title={valor != null && 'completo' in kpi ? kpi.completo?.(valor) : undefined}
                >
                  {valor != null ? kpi.format(valor) : '—'}
                </p>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
